import {
  DIMENSION_POSITION_ATTRIBUTE,
  POSITION_COMPLEMENTS,
  RELATED_DIMENSION,
} from './dimensions';
import { intersectBoundingBoxes, getAvailableSpace } from './utils';

export const getPrimaryAxisPosition = ({
  anchorBoundingBox,
  arrowConfig,
  offset,
  popoverBoundingBox,
  position,
}) => {
  // If the position is to the top or left, the target position decreases
  // from the anchor's top or left, otherwise the position
  // adds to the anchor's
  const isOffsetDecreasing = position === 'top' || position === 'left';

  const primaryAxisDimension = RELATED_DIMENSION[position]; // 'top' -> 'height'
  const popoverSizeOnPrimaryAxis = popoverBoundingBox[primaryAxisDimension];

  // Start at the top or left edge of anchor element
  const primaryAxisPositionName =
    DIMENSION_POSITION_ATTRIBUTE[primaryAxisDimension]; // 'height' => 'top'
  const anchorEdgeOrigin = anchorBoundingBox[primaryAxisPositionName];

  // Find the popover position on the primary axis
  const anchorSizeOnPrimaryAxis = anchorBoundingBox[primaryAxisDimension];
  const primaryAxisOffset = isOffsetDecreasing
    ? popoverSizeOnPrimaryAxis
    : anchorSizeOnPrimaryAxis;

  const contentOffset =
    (offset + primaryAxisOffset) * (isOffsetDecreasing ? -1 : 1);
  const primaryAxisPosition = anchorEdgeOrigin + contentOffset;

  let primaryAxisArrowPosition;
  if (arrowConfig) {
    primaryAxisArrowPosition = isOffsetDecreasing
      ? popoverSizeOnPrimaryAxis
      : 0;
  }

  return { primaryAxisPosition, primaryAxisArrowPosition };
};

export const getCrossAxisPosition = ({
  align,
  anchorBoundingBox,
  arrowConfig,
  buffer,
  containerBoundingBox,
  crossAxisDimension,
  crossAxisFirstSide,
  crossAxisSecondSide,
  offset,
  popoverBoundingBox,
  position,
  windowBoundingBox,
}) => {
  // How much of the popover overflows past either side of the anchor
  // if it is centered
  const popoverSizeOnCrossAxis = popoverBoundingBox[crossAxisDimension];
  const anchorSizeOnCrossAxis = anchorBoundingBox[crossAxisDimension];
  const anchorHalfSize = anchorSizeOnCrossAxis / 2;

  // The popover's original position on the cross-axis is determined by:
  const crossAxisPositionOriginal =
    anchorBoundingBox[crossAxisFirstSide] + // Where the anchor is located
    anchorHalfSize - // Plus half the anchor dimension
    popoverSizeOnCrossAxis / 2; // less half the popover dimension

  // To fit the conent within both the window and container computer
  // the smaller of the two spaces along each edge
  const combinedBoundingBox = intersectBoundingBoxes(
    windowBoundingBox,
    containerBoundingBox
  );

  const availableSpace = getAvailableSpace(
    anchorBoundingBox,
    combinedBoundingBox,
    buffer,
    offset,
    position
  );

  const minimumSpace = arrowConfig ? arrowConfig.arrowBuffer : 0;
  const contentOverflowSize =
    (popoverSizeOnCrossAxis - anchorSizeOnCrossAxis) / 2;

  let alignAmount = 0;
  let alignDirection = 1;
  let amountOfShiftNeeded = 0;
  let shiftDirection = 1;

  if (align != null) {
    // No alignment, find how much the container boundary requires
    // the content to shift
    alignDirection = align === 'top' || align === 'left' ? 1 : -1;
    alignAmount = contentOverflowSize;

    const alignedOverflowAmount = contentOverflowSize + alignAmount;
    const needsShift =
      alignedOverflowAmount > availableSpace[POSITION_COMPLEMENTS[align]];
    amountOfShiftNeeded = needsShift
      ? alignedOverflowAmount - availableSpace[POSITION_COMPLEMENTS[align]]
      : 0;
    shiftDirection = -1 * alignDirection;
  } else {
    // shifting the popover to one side may yield a better fit
    const spaceAvailableOnFirstSide = availableSpace[crossAxisFirstSide];
    const spaceAvailableOnSecondSide = availableSpace[crossAxisSecondSide];

    const isShiftedTowardsFirstSide =
      spaceAvailableOnFirstSide > spaceAvailableOnSecondSide;
    shiftDirection = isShiftedTowardsFirstSide ? -1 : 1;

    // Determine which direction has more room and the popover
    // should shift to
    const leastAvailableSpace = Math.min(
      spaceAvailableOnFirstSide,
      spaceAvailableOnSecondSide
    );

    const needsShift = contentOverflowSize > leastAvailableSpace;
    amountOfShiftNeeded = needsShift
      ? contentOverflowSize - leastAvailableSpace
      : 0;
  }

  // Shift over popover if necessary
  const shiftAmount = amountOfShiftNeeded * shiftDirection;
  let crossAxisPosition =
    crossAxisPositionOriginal + shiftAmount + alignAmount * alignDirection;

  // if an arrowConfig is provided, find where to position the arrow
  let crossAxisArrowPosition;
  if (arrowConfig) {
    const { arrowWidth } = arrowConfig;
    crossAxisArrowPosition =
      anchorBoundingBox[crossAxisFirstSide] + anchorHalfSize - arrowWidth / 2;

    // Make sure there's enough buffer around the arrow by
    // calculating how much the arrow would need to move
    // but instead of moving the arrow, shift the popover content
    if (crossAxisArrowPosition < crossAxisPosition + minimumSpace) {
      // arrow is too close to the minimum side
      const diff = crossAxisPosition + minimumSpace - crossAxisArrowPosition;
      crossAxisPosition -= diff;
    } else if (
      crossAxisArrowPosition + minimumSpace + arrowWidth >
      crossAxisPosition + popoverSizeOnCrossAxis
    ) {
      // arrow is too close to maximum side
      const edge = crossAxisPosition + popoverSizeOnCrossAxis;
      const diff = crossAxisArrowPosition - (edge - minimumSpace - arrowWidth);
      crossAxisPosition += diff;
    }
  }

  return { crossAxisPosition, crossAxisArrowPosition };
};
